/**
 * SDTestApp Hub production API — full wizard, slots, unattended chain.
 */

import { buildSdTestAppFetchBaseUrl, SDTESTAPP_DEFAULT_PORT } from "./sdTestAppHttpClient";

export interface ProductionSequenceInfo {
  name: string;
  setName: string;
}

export interface ProductionWorkflowState {
  stepIndex: number;
  maxStepIndexReached: number;
  stepTitles: string[];
  unattendedPhase: string;
  ready500aConfirmed: boolean;
  slowResetInfo?: string | null;
  calibrationEstimatedSeconds?: number | null;
  runAllTestsEstimatedSeconds?: number | null;
  unattendedWorkerStarted?: boolean;
  runHolderKey?: string | null;
  runHolderDisplayName?: string | null;
  runHolderBlockingActive?: boolean;
}

export interface ProductionRunHolderStatus {
  blockingActive: boolean;
  blocked: boolean;
  isHolder: boolean;
  holderKey?: string | null;
  holderDisplayName?: string | null;
  stepIndex: number;
  success?: boolean;
  error?: string;
}

export interface ProductionConfig {
  laborPage: string;
  assignedSlots: string[];
  hasReferenceDevice: boolean;
  workflow: ProductionWorkflowState;
  deviceCalibrationSequence: ProductionSequenceInfo;
  productionTestSequence: ProductionSequenceInfo;
  stationAfterCalibration: string | null;
  stationAfterProductionTests: string | null;
}

export interface ProductionSlotRow {
  slotName: string;
  connectorId: string | null;
  status?: string | null;
  tooltip?: string;
}

export interface RackLayoutPosition {
  slotName: string;
  x: number;
  y: number;
}

export interface RackLayoutState {
  coilShape: string;
  positions: RackLayoutPosition[];
  viewBox: { minX: number; minY: number; width: number; height: number };
}

export interface ProductionSlotsState {
  layoutVersion: number;
  slots: ProductionSlotRow[];
  poolOnlyConnectorIds: string[];
  referenceConnectorId: string | null;
  hasReferenceDevice: boolean;
  rackLayout?: RackLayoutState;
}

export interface ProductionReviewSummary {
  completionEvent?: Record<string, unknown> | null;
  verificationResults: unknown[];
  downloadText: string;
  hasCompletionEvent: boolean;
  hasVerificationResults: boolean;
}

export interface ProductionBatchSlotStatus {
  connectorId: string;
  status: string;
  error?: string | null;
}

export interface ProductionBatchStatus {
  isRunning: boolean;
  testId?: string | null;
  sequenceId?: string | null;
  sequenceSet?: string | null;
  currentStep?: number | null;
  totalSteps?: number | null;
  slots?: Record<string, ProductionBatchSlotStatus>;
}

export interface ProductionUnattendedStatus extends ProductionWorkflowState {
  running: boolean;
  phase: string;
  batch: ProductionBatchStatus;
}

export interface ProductionActionResult {
  success: boolean;
  error?: string;
  testId?: string;
  amps?: number;
  maxSeconds?: number;
}

function normalizeFetchError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

export class SdTestAppProductionClient {
  private readonly baseUrl: string;

  constructor(port: string = SDTESTAPP_DEFAULT_PORT) {
    this.baseUrl = buildSdTestAppFetchBaseUrl(port);
  }

  async fetchConfig(): Promise<ProductionConfig> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/config`);
    if (!response.ok) {
      throw new Error(`Production config failed (${response.status})`);
    }
    return (await response.json()) as ProductionConfig;
  }

  async fetchWorkflow(): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/workflow`);
    if (!response.ok) {
      throw new Error(`Workflow failed (${response.status})`);
    }
    return (await response.json()) as ProductionWorkflowState;
  }

  async setWorkflowStep(stepIndex: number): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/workflow/step`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stepIndex }),
    });
    const payload = (await response.json()) as ProductionWorkflowState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Set step failed (${response.status})`));
    }
    return payload;
  }

  async resetWorkflow(): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/workflow/reset`, { method: "POST" });
    const payload = (await response.json()) as ProductionWorkflowState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Reset workflow failed (${response.status})`));
    }
    return payload;
  }

  async confirm500aReady(): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/confirm-500a-ready`, { method: "POST" });
    const payload = (await response.json()) as ProductionWorkflowState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Confirm 500A ready failed (${response.status})`));
    }
    return payload;
  }

  async fetchSlots(): Promise<ProductionSlotsState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/slots`);
    if (!response.ok) {
      throw new Error(`Slots failed (${response.status})`);
    }
    return (await response.json()) as ProductionSlotsState;
  }

  async assignSlot(slotName: string, connectorId: string | null): Promise<ProductionSlotsState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/slots/${encodeURIComponent(slotName)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ connectorId }),
    });
    const payload = (await response.json()) as ProductionSlotsState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Assign slot failed (${response.status})`));
    }
    return payload;
  }

  async clearSlot(slotName: string): Promise<ProductionSlotsState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/slots/${encodeURIComponent(slotName)}`, {
      method: "DELETE",
    });
    const payload = (await response.json()) as ProductionSlotsState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Clear slot failed (${response.status})`));
    }
    return payload;
  }

  async syncSlots(): Promise<ProductionSlotsState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/slots/sync`, { method: "POST" });
    const payload = (await response.json()) as ProductionSlotsState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Sync slots failed (${response.status})`));
    }
    return payload;
  }

  async setReferenceDevice(connectorId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/reference-device`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ connectorId }),
    });
    const payload = (await response.json()) as { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Set reference failed (${response.status})`));
    }
  }

  async startScannerFastScan(): Promise<void> {
    await fetch(`${this.baseUrl}/api/hub/v1/production/scanner/fast-scan/start`, { method: "POST" });
  }

  async stopScannerFastScan(): Promise<void> {
    await fetch(`${this.baseUrl}/api/hub/v1/production/scanner/fast-scan/stop`, { method: "POST" });
  }

  async startCoilCalibration(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/coil-calibration/start`, { method: "POST" });
    const payload = (await response.json()) as { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Coil calibration start failed (${response.status})`));
    }
  }

  async fetchCoilCalibrationStatus(): Promise<{ running: boolean; text: string }> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/coil-calibration/status`);
    if (!response.ok) {
      throw new Error(`Coil calibration status failed (${response.status})`);
    }
    return (await response.json()) as { running: boolean; text: string };
  }

  async startUnattended(): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/unattended/start`, { method: "POST" });
    const payload = (await response.json()) as ProductionWorkflowState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Start unattended failed (${response.status})`));
    }
    return payload;
  }

  async fetchUnattendedStatus(): Promise<ProductionUnattendedStatus> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/unattended/status`);
    if (!response.ok) {
      throw new Error(`Unattended status failed (${response.status})`);
    }
    return (await response.json()) as ProductionUnattendedStatus;
  }

  async cancelUnattended(): Promise<ProductionWorkflowState> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/unattended/cancel`, { method: "POST" });
    const payload = (await response.json()) as ProductionWorkflowState & { error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Cancel unattended failed (${response.status})`));
    }
    return payload;
  }

  async publishReview(): Promise<ProductionReviewSummary & { success: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/review/publish`, { method: "POST" });
    const payload = (await response.json()) as ProductionReviewSummary & { success: boolean; error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Publish review failed (${response.status})`));
    }
    return payload;
  }

  async fetchReviewSummary(): Promise<ProductionReviewSummary> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/review/summary`);
    if (!response.ok) {
      throw new Error(`Review summary failed (${response.status})`);
    }
    return (await response.json()) as ProductionReviewSummary;
  }

  async fetchVisibleLaborMcuUuids(): Promise<string[]> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/labor/visible-mcu-uuids`);
    if (!response.ok) {
      throw new Error(`Visible labor MCU list failed (${response.status})`);
    }
    const payload = (await response.json()) as { mcuUuids?: string[] };
    return payload.mcuUuids ?? [];
  }

  async fetchBatchStatus(): Promise<ProductionBatchStatus> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/batch/status`);
    if (!response.ok) {
      throw new Error(`Batch status failed (${response.status})`);
    }
    return (await response.json()) as ProductionBatchStatus;
  }

  async stopBatch(): Promise<ProductionBatchStatus> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/batch/stop`, { method: "POST" });
    const payload = (await response.json()) as { status?: ProductionBatchStatus; error?: string };
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Batch stop failed (${response.status})`));
    }
    return payload.status ?? (await this.fetchBatchStatus());
  }

  async powerPulse(amps = 500, maxSeconds = 3): Promise<ProductionActionResult> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api/hub/v1/production/power-pulse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amps, maxSeconds }),
      });
    } catch (error) {
      throw new Error(normalizeFetchError(error));
    }
    const payload = (await response.json()) as ProductionActionResult & { error?: string };
    if (!response.ok) {
      return { success: false, error: String(payload.error ?? `Power pulse failed (${response.status})`) };
    }
    return payload;
  }

  async fetchRunHolderStatus(userId: string): Promise<ProductionRunHolderStatus> {
    const query = new URLSearchParams({ userId });
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/run-holder?${query.toString()}`);
    if (!response.ok) {
      throw new Error(`Run holder status failed (${response.status})`);
    }
    return (await response.json()) as ProductionRunHolderStatus;
  }

  async claimRunHolder(userId: string, displayName: string): Promise<ProductionRunHolderStatus> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/run-holder/claim`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, displayName }),
    });
    const payload = (await response.json()) as ProductionRunHolderStatus;
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Run holder claim failed (${response.status})`));
    }
    return payload;
  }

  async releaseRunHolder(userId: string): Promise<ProductionRunHolderStatus> {
    const response = await fetch(`${this.baseUrl}/api/hub/v1/production/run-holder/release`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId }),
    });
    const payload = (await response.json()) as ProductionRunHolderStatus;
    if (!response.ok) {
      throw new Error(String(payload.error ?? `Run holder release failed (${response.status})`));
    }
    return payload;
  }

  async fetchSelectiveRetestCapability(): Promise<{ supported: boolean }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/hub/v1/production/batch/selective-retest/capability`);
      if (response.status === 404) {
        return { supported: false };
      }
      if (!response.ok) {
        return { supported: false };
      }
      const payload = (await response.json()) as { supported?: boolean };
      return { supported: payload.supported === true };
    } catch {
      return { supported: false };
    }
  }

  async startSelectiveRetest(input: {
    connectorId: string;
    testKeys: string[];
  }): Promise<ProductionActionResult> {
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api/hub/v1/production/batch/selective-retest/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
    } catch (error) {
      return { success: false, error: normalizeFetchError(error) };
    }
    const payload = (await response.json()) as ProductionActionResult & { error?: string };
    if (!response.ok) {
      return { success: false, error: String(payload.error ?? `Selective retest failed (${response.status})`) };
    }
    return payload;
  }
}
